import { GAS_PRICE_TYPE, RPC_AUTHENTICATION, type ChainInfo } from '@safe-global/safe-gateway-typescript-sdk'
import {
  getCompatibilityFallbackHandlerDeployment,
  getCreateCallDeployment,
  getMultiSendCallOnlyDeployment,
  getMultiSendDeployment,
  getProxyFactoryDeployment,
  getSafeL2SingletonDeployment,
  getSignMessageLibDeployment,
  getSimulateTxAccessorDeployment,
  type SingletonDeployment,
} from '@safe-global/safe-deployments'

export const APPCHAIN_CHAIN_ID = '466'
export const APPCHAIN_SHORT_NAME = 'appchain'
export const APPCHAIN_PUBLIC_RPC_URL = 'https://rpc.appchain.xyz/http'
export const APPCHAIN_SAFE_VERSION = '1.3.0'

const deploymentAddress = (
  getter: (filter?: { version?: string; network?: string }) => SingletonDeployment | undefined,
): string | null => {
  const deployment = getter({ version: APPCHAIN_SAFE_VERSION, network: APPCHAIN_CHAIN_ID })
  return deployment?.networkAddresses[APPCHAIN_CHAIN_ID] ?? deployment?.defaultAddress ?? null
}

const contractAddresses = {
  createCallAddress: deploymentAddress(getCreateCallDeployment),
  fallbackHandlerAddress: deploymentAddress(getCompatibilityFallbackHandlerDeployment),
  multiSendAddress: deploymentAddress(getMultiSendDeployment),
  multiSendCallOnlyAddress: deploymentAddress(getMultiSendCallOnlyDeployment),
  safeProxyFactoryAddress: deploymentAddress(getProxyFactoryDeployment),
  safeSingletonAddress: deploymentAddress(getSafeL2SingletonDeployment),
  safeWebAuthnSignerFactoryAddress: null,
  signMessageLibAddress: deploymentAddress(getSignMessageLibDeployment),
  simulateTxAccessorAddress: deploymentAddress(getSimulateTxAccessorDeployment),
}

export const getAppchainRpcUrl = (): string => {
  const apiKey = process.env.NEXT_PUBLIC_THIRDWEB_API_KEY || process.env.NEXT_PUBLIC_THIRDWEB_CLIENT_ID || ''
  return apiKey ? `https://466.rpc.thirdweb.com/${apiKey}` : APPCHAIN_PUBLIC_RPC_URL
}

export const getAppchainChainInfo = (): ChainInfo => {
  const rpcUrl = getAppchainRpcUrl()

  return {
    chainId: APPCHAIN_CHAIN_ID,
    chainName: 'AppChain',
    description: 'AppChain is an Arbitrum Nitro L2',
    chainLogoUri: 'https://safe-transaction-assets.safe.global/chains/1/chain_logo.png',
    l2: true,
    isTestnet: false,
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
      logoUri: 'https://safe-transaction-assets.safe.global/chains/1/currency_logo.png',
    },
    transactionService: '',
    blockExplorerUriTemplate: {
      address: 'https://explorer.appchain.xyz/address/{{address}}',
      txHash: 'https://explorer.appchain.xyz/tx/{{txHash}}',
      api: 'https://explorer.appchain.xyz/api?module={{module}}&action={{action}}&address={{address}}&apiKey={{apiKey}}',
    },
    disabledWallets: [],
    ensRegistryAddress: null,
    features: ['COUNTERFACTUAL', 'EIP1271', 'EIP1559', 'ERC721', 'NATIVE_WALLETCONNECT'],
    gasPrice: [{ type: GAS_PRICE_TYPE.FIXED, weiValue: '10000000' }],
    publicRpcUri: {
      authentication: RPC_AUTHENTICATION.NO_AUTHENTICATION,
      value: APPCHAIN_PUBLIC_RPC_URL,
    },
    rpcUri: {
      authentication: RPC_AUTHENTICATION.NO_AUTHENTICATION,
      value: rpcUrl,
    },
    safeAppsRpcUri: {
      authentication: RPC_AUTHENTICATION.NO_AUTHENTICATION,
      value: rpcUrl,
    },
    shortName: APPCHAIN_SHORT_NAME,
    theme: {
      textColor: '#ffffff',
      backgroundColor: '#111827',
    },
    contractAddresses,
    balancesProvider: {
      chainName: null,
      enabled: false,
    },
  } as ChainInfo
}
